import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Stack,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  Select,
  ListItemIcon,
  Checkbox,
  Button,
  Toolbar,
} from "@mui/material";
import {
  InsertDriveFile as FileIcon,
  Folder as FolderIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  DeleteTwoTone as TrashIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as AscIcon,
  ArrowDownward as DescIcon,
} from "@mui/icons-material";
import { FileItemDto } from "./FileManager.tsx";
import { formatFileSize, formatDate } from "../utils/formatUtils.ts";
import { SimpleAlertDialog } from "./SimpleAlertDialog.tsx";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { PaletteColor } from "../types/PaletteColor.ts";
import { LoadingOverlay } from "./loadingOverlay.tsx";

export interface TrashFolderDto {
  folderId: string;
  folderName: string;
  deletedAt: string;
  createdBy: number;
}

interface BatchItem {
  id: string;
  type: "file" | "folder";
}

interface TrashContentProps {
  title: string;
  files: FileItemDto[];
  folders: TrashFolderDto[];
  isLoading: boolean;
  palette: PaletteColor;
  isMobile?: boolean;
  onRestore: (id: string, type: "file" | "folder") => Promise<void>;
  onHardDelete: (id: string, type: "file" | "folder") => Promise<void>;
  onBatchRestore: (items: BatchItem[]) => Promise<void>;
  onBatchHardDelete: (items: BatchItem[]) => Promise<void>;
}

type SortKey = "name" | "deletedAt" | "size";
type SortOrder = "asc" | "desc";

type TrashRow =
  | { kind: "folder"; id: string; name: string; deletedAt: string; size: number; data: TrashFolderDto }
  | { kind: "file"; id: string; name: string; deletedAt: string; size: number; data: FileItemDto };

const sortKeyLabels: Record<SortKey, string> = {
  name: "名前",
  deletedAt: "削除日時",
  size: "サイズ",
};

const formatDateShort = (dateString: string): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("ja-JP", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const TrashContent: React.FC<TrashContentProps> = ({
  title,
  files,
  folders,
  isLoading,
  palette,
  isMobile = false,
  onRestore,
  onHardDelete,
  onBatchRestore,
  onBatchHardDelete,
}) => {
  const { accentColor, isDarkMode } = usePaletteColors(palette);
  const [confirmTarget, setConfirmTarget] = useState<{
    id: string;
    type: "file" | "folder";
    name: string;
  } | null>(null);
  const [batchDeleteConfirm, setBatchDeleteConfirm] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("deletedAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<TrashRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, row: TrashRow) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTarget(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const sortedRows = useMemo<TrashRow[]>(() => {
    const rows: TrashRow[] = [
      ...folders.map((f) => ({
        kind: "folder" as const,
        id: f.folderId,
        name: f.folderName,
        deletedAt: f.deletedAt,
        size: -1,
        data: f,
      })),
      ...files.map((f) => ({
        kind: "file" as const,
        id: f.fileId,
        name: f.fileName,
        deletedAt: f.updatedAt,
        size: f.fileSize,
        data: f,
      })),
    ];

    return rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") {
        cmp = a.name.localeCompare(b.name, "ja");
      } else if (sortKey === "deletedAt") {
        cmp = a.deletedAt.localeCompare(b.deletedAt);
      } else if (sortKey === "size") {
        cmp = a.size - b.size;
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });
  }, [folders, files, sortKey, sortOrder]);

  const allIds = useMemo(() => sortedRows.map((r) => r.id), [sortedRows]);
  const isAllSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const isIndeterminate = !isAllSelected && allIds.some((id) => selectedIds.has(id));

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedItems = useMemo<BatchItem[]>(
    () =>
      sortedRows
        .filter((r) => selectedIds.has(r.id))
        .map((r) => ({ id: r.id, type: r.kind })),
    [sortedRows, selectedIds],
  );

  const handleBatchRestore = async () => {
    await onBatchRestore(selectedItems);
    setSelectedIds(new Set());
  };

  const handleBatchHardDelete = async () => {
    await onBatchHardDelete(selectedItems);
    setSelectedIds(new Set());
    setBatchDeleteConfirm(false);
  };

  if (isLoading) return <LoadingOverlay message="Now Loading..." color={palette} />;

  const isEmpty = files.length === 0 && folders.length === 0;

  return (
    <Box sx={{ p: isMobile ? 2 : 4, height: "100%", overflowY: "auto" }}>
      <Box
        sx={{
          pb: 1.5,
          mb: 2,
          borderBottom: "2px dashed",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <TrashIcon sx={{ color: accentColor, fontSize: 24 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? "grey.400" : "grey.800",
            }}
          >
            {title}
          </Typography>
        </Stack>
      </Box>

      {/* 一括操作ツールバー */}
      {selectedIds.size > 0 && (
        <Toolbar
          variant="dense"
          sx={{
            mb: 1,
            px: 1,
            borderRadius: 1,
            bgcolor: "action.selected",
            minHeight: 40,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {selectedIds.size}件を選択中
          </Typography>
          <Button
            size="small"
            startIcon={<RestoreIcon />}
            color="primary"
            onClick={handleBatchRestore}
            sx={{ mr: 1 }}
          >
            一括復元
          </Button>
          <Button
            size="small"
            startIcon={<DeleteForeverIcon />}
            color="error"
            onClick={() => setBatchDeleteConfirm(true)}
          >
            一括削除
          </Button>
        </Toolbar>
      )}

      {isEmpty ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, pl: 2 }}>
          削除されたアイテムはありません。
        </Typography>
      ) : isMobile ? (
        <>
          {/* モバイル用ソートコントロール */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 0 }}>
              <Select
                value={sortKey}
                onChange={(e) => handleSort(e.target.value as SortKey)}
                variant="standard"
                disableUnderline
                sx={{
                  fontSize: "0.8rem",
                  color: "text.secondary",
                  "& .MuiSelect-select": { py: 0, pr: "20px !important" },
                }}
              >
                {(Object.keys(sortKeyLabels) as SortKey[]).map((key) => (
                  <MenuItem key={key} value={key} dense>
                    {sortKeyLabels[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              onClick={() => setSortOrder((d) => (d === "asc" ? "desc" : "asc"))}
              sx={{ color: "text.secondary" }}
            >
              {sortOrder === "asc" ? (
                <AscIcon sx={{ fontSize: 18 }} />
              ) : (
                <DescIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Stack>

          {/* モバイル用リスト */}
          <Box>
            {sortedRows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedIds.has(row.id)}
                  onChange={() => toggleSelect(row.id)}
                  sx={{ mr: 0.5, p: 0.5 }}
                />
                <Box sx={{ flexShrink: 0, mr: 1.5, display: "flex", alignItems: "center" }}>
                  {row.kind === "folder" ? (
                    <FolderIcon fontSize="small" color="action" />
                  ) : (
                    <FileIcon fontSize="small" color="action" />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {row.name}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {formatDateShort(row.deletedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">·</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.kind === "file" ? formatFileSize(row.size) : "-"}
                    </Typography>
                  </Stack>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, row)}
                  sx={{ flexShrink: 0 }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          {/* 三点メニュー */}
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() => {
                if (menuTarget) onRestore(menuTarget.id, menuTarget.kind);
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <RestoreIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <Typography variant="body2" color="primary">復元</Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (menuTarget) {
                  setConfirmTarget({ id: menuTarget.id, type: menuTarget.kind, name: menuTarget.name });
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <DeleteForeverIcon fontSize="small" color="error" />
              </ListItemIcon>
              <Typography variant="body2" color="error">削除</Typography>
            </MenuItem>
          </Menu>
        </>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead sx={{ bgcolor: "action.hover" }}>
              <TableRow>
                <TableCell padding="checkbox" width={40}>
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortKey === "name"}
                    direction={sortKey === "name" ? sortOrder : "asc"}
                    onClick={() => handleSort("name")}
                  >
                    名前
                  </TableSortLabel>
                </TableCell>
                <TableCell width={160}>
                  <TableSortLabel
                    active={sortKey === "deletedAt"}
                    direction={sortKey === "deletedAt" ? sortOrder : "asc"}
                    onClick={() => handleSort("deletedAt")}
                  >
                    削除日時
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" width={80} sx={{ whiteSpace: "nowrap" }}>
                  <TableSortLabel
                    active={sortKey === "size"}
                    direction={sortKey === "size" ? sortOrder : "asc"}
                    onClick={() => handleSort("size")}
                  >
                    サイズ
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" width={160}>
                  操作
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  selected={selectedIds.has(row.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {row.kind === "folder" ? (
                        <FolderIcon fontSize="small" color="action" />
                      ) : (
                        <FileIcon fontSize="small" color="action" />
                      )}
                      <Typography variant="body2">{row.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(row.deletedAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {row.kind === "file" ? (
                      <Typography variant="caption">{formatFileSize(row.size)}</Typography>
                    ) : (
                      <Typography variant="caption" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="復元">
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.25}
                          sx={{ cursor: "pointer", color: "primary.main" }}
                          onClick={() => onRestore(row.id, row.kind)}
                        >
                          <IconButton size="small" color="primary">
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" color="primary" sx={{ whiteSpace: "nowrap" }}>復元</Typography>
                        </Stack>
                      </Tooltip>
                      <Tooltip title="完全に削除">
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.25}
                          sx={{ cursor: "pointer", color: "error.main" }}
                          onClick={() =>
                            setConfirmTarget({ id: row.id, type: row.kind, name: row.name })
                          }
                        >
                          <IconButton size="small" color="error">
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" color="error" sx={{ whiteSpace: "nowrap" }}>削除</Typography>
                        </Stack>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 単体削除確認 */}
      <SimpleAlertDialog
        open={!!confirmTarget}
        title="完全に削除"
        message={`「${confirmTarget?.name}」を完全に削除しますか？この操作は元に戻せません。`}
        confirmLabel="完全に削除"
        severity="error"
        onConfirm={() => {
          if (confirmTarget) {
            onHardDelete(confirmTarget.id, confirmTarget.type);
          }
        }}
        onClose={() => setConfirmTarget(null)}
      />

      {/* 一括削除確認 */}
      <SimpleAlertDialog
        open={batchDeleteConfirm}
        title="一括完全削除"
        message={`選択した${selectedItems.length}件を完全に削除しますか？この操作は元に戻せません。`}
        confirmLabel="完全に削除"
        severity="error"
        onConfirm={handleBatchHardDelete}
        onClose={() => setBatchDeleteConfirm(false)}
      />
    </Box>
  );
};
